'use client';

import { MinusMini, PlusMini } from '@medusajs/icons';
import { clx } from '@medusajs/ui';
import { useEffect, useRef, useState } from 'react';

import { useDebounce } from '@lib/hooks/use-debounce';

interface CounterProps {
  count: number;
  className?: string;
  disabled?: boolean;
  delay?: number;
  max?: number;
  errMessage?: string;
  'data-testid'?: string;
  onChange: (count: number) => void;
}

const Counter = ({
  count,
  className,
  disabled,
  delay = 0,
  max,
  errMessage,
  'data-testid': testId,
  onChange,
}: CounterProps) => {
  const memoizedCount = useRef(count);
  const [input, setInput] = useState(`${count}`);
  // Tracks the last value sent via onChange so we can detect stale server data
  const pendingValue = useRef<number | null>(null);

  const handleIncrement = () => {
    if (disabled || (max && (count >= max || +input >= max))) {
      return;
    }
    setInput(`${+input + 1}`);
  };

  const handleDecrement = () => {
    if (disabled || count <= 1 || +input <= 1) {
      return;
    }
    setInput(`${+input - 1}`);
  };

  const handleInputChange = ({
    target: { value },
  }: React.ChangeEvent<HTMLInputElement>) => {
    if (disabled) {
      return;
    }
    // rule: only allow number, and first digit cannot be 0
    if (/^[1-9]\d*$/.test(value)) {
      setInput(`${Math.min.apply(null, [+value, ...(max ? [max] : [])])}`);
    } else if (value === '') {
      setInput('');
    }
  };

  const debouncedInput = useDebounce(input, delay);

  const handleOnBlur = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.value === '') {
      setInput(`${count}`);
    }
  };

  useEffect(() => {
    if (debouncedInput === `${memoizedCount.current}`) {
      return;
    }
    if (debouncedInput !== '') {
      const value = parseInt(debouncedInput);
      pendingValue.current = value;
      onChange(value);
    }
  }, [debouncedInput, onChange]);

  useEffect(() => {
    const prevCount = memoizedCount.current;
    memoizedCount.current = count;

    // If we sent a value via onChange that the parent hasn't acknowledged yet
    // (count doesn't match pendingValue), skip syncing to avoid overwriting
    // the user's local input with stale server data.
    if (pendingValue.current !== null && count !== pendingValue.current) {
      return;
    }
    pendingValue.current = null;

    // If the user has local edits (input differs from the previous count),
    // don't overwrite with the incoming count.
    setInput((prev) => {
      const hasLocalEdits = prev !== `${prevCount}` && prev !== '';
      return hasLocalEdits ? prev : `${count}`;
    });
  }, [count]);

  return (
    <div className={clx(className)}>
      <div
        className={clx('flex select-none justify-between rounded border p-2', {
          'border-red-30 border': errMessage,
        })}
      >
        <MinusMini
          className={clx('cursor-pointer', {
            'cursor-not-allowed text-gray-400': disabled || +input <= 1,
          })}
          data-testid={testId ? `${testId}-decrement` : undefined}
          onClick={handleDecrement}
        />
        <input
          className="max-w-[40px] flex-auto bg-transparent text-center focus:outline-none"
          type="tel"
          data-testid={testId ? `${testId}-input` : undefined}
          onChange={handleInputChange}
          onBlur={handleOnBlur}
          value={input}
        />

        <PlusMini
          className={clx('cursor-pointer', {
            'cursor-not-allowed text-gray-400':
              disabled || (max !== undefined && +input >= max),
          })}
          data-testid={testId ? `${testId}-increment` : undefined}
          onClick={handleIncrement}
        />
      </div>
      {errMessage && (
        <div
          className="text-red-30 mt-1 text-center text-xs"
          data-testid={testId ? `${testId}-error` : undefined}
        >
          {errMessage}
        </div>
      )}
    </div>
  );
};

export default Counter;
